import { Component, OnInit, ViewChild } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Router } from '@angular/router';
import { EmailCampaign, EmailCampaignIdLabel, EmailCampaignStatus, EmailStatusHTMLTagSpan } from '@models/email/email-campaign';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { AtlasToastService } from '@services/atlas-toast.service';
import { EmailCampaignListService } from '@services/email-campaign-list.service';
import { AtlasGridOptions } from '@ui/components/atlas-grid/altas-grid-options.model';
import { AtlasGridComponent } from '@ui/components/atlas-grid/atlas-grid.component';
import { ButtonGroupRendererComponent } from '@ui/components/atlas-grid/renderer/buttongroup-renderer.component';
import { AtlasGridHtmlRendererComponent } from '@ui/components/atlas-grid/renderer/html-renderer.component';
import { flattenObject } from '@utils/utils';
import { ToastContainerDirective } from 'ngx-toastr';
import { finalize, map } from 'rxjs/operators';
import { LoaderService } from '@services/loader.service';

@Component({
  selector: 'wsb-email-campaigns-tab-list',
  templateUrl: './email-campaigns-list.component.html',
  styleUrls: ['./email-campaigns-list.component.scss'],
  animations: [
    trigger('gridIsLoaded', [
      state('hide', style({ opacity: 0 })),
      state('show', style({ opacity: 1 })),
      transition('hide => show', animate('150ms', style({ opacity: 1 }))),
      transition('show => hide', animate('150ms', style({ opacity: 0 })))
    ])
  ],
  standalone: false
})
export class EmailCampaignsListComponent implements OnInit {
  @ViewChild(AtlasGridComponent) grid: AtlasGridComponent;
  @ViewChild(ToastContainerDirective) toastContainer: ToastContainerDirective;
  @ViewChild('modalArchive') modalArchive;

  components = {
    buttonGroupRenderer: ButtonGroupRendererComponent,
    htmlRenderer: AtlasGridHtmlRendererComponent
  };

  tableColumnDefs = null;
  tableGridOptions = null;
  tableRowData = null;
  countToActiveList;

  modalRefArchive: NgbModalRef;
  gridAnimationState = 'hide';
  private stringEmpty = '';
  private idCampaignToArchive;

  constructor(
    private emailListService: EmailCampaignListService,
    private translateService: TranslateService,
    private loaderService: LoaderService,
    private modalService: NgbModal,
    private atlasToast: AtlasToastService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.createTable();
    this.gridOptionDefinition();
  }

  private gridOptionDefinition() {
    // const that = this;
    this.tableGridOptions = AtlasGridOptions.deserialize({
      pagination: true,
      disablePagination: true,
      rowHeight: 80
    });
    this.reloadList();
  }

  private reloadList() {
    this.loaderService.show();
    this.emailListService
      .getListCampaignEmail()
      .pipe(
        finalize(() => this.loaderService.hide()),
        map(list => {
          return this.getFilterListByStatus(list);
        })
      )
      .subscribe(data => {
        this.tableRowData = data.listToshow.map(d => flattenObject(d));
        this.countToActiveList = '(' + data.countToActive + ')';
        this.gridAnimationState = 'show';
      });
  }

  private getFilterListByStatus(data) {
    const listToshow = data.filter(d => d.status === EmailCampaignStatus.ACTIVE || d.status === EmailCampaignStatus.ARCHIVED);
    const countToActive = data.filter(d => d.status === EmailCampaignStatus.TO_ACTIVATE).length;
    return { listToshow, countToActive };
  }

  private createTable() {
    this.tableColumnDefs = [
      {
        headerNameTranslate: 'mail_campaign_setup_title_input',
        field: 'title',
        sortable: false,
        suppressCellFocus: true,
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              const data = params.data;
              return this.getHtmlRenderingTitleAndDescription(data);
            }
            return this.stringEmpty;
          }
        },
        maxWidth: 360
      },
      {
        headerNameTranslate: 'common_status',
        field: 'status',
        sortable: false,
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              const data = params.data;
              return this.getHtmlRenderingStatus(data);
            }
            return this.stringEmpty;
          }
        },
        maxWidth: 300
      },
      {
        headerNameTranslate: 'mail_campaigns_language',
        field: 'idLanguage',
        sortable: false,
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => (params && params.data ? params.data.idLanguage : this.stringEmpty)
        },
        maxWidth: 300
      },
      {
        headerNameTranslate: 'sender_sender',
        field: 'emailSender',
        sortable: false,
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => (params && params.data ? params.data.emailSender : this.stringEmpty)
        },
        maxWidth: 300
      },
      {
        headerNameTranslate: 'common_actions',
        suppressCellFocus: true,
        resizable: false,
        cellRenderer: 'buttonGroupRenderer',
        cellRendererParams: {
          hide: param => {
            if (param) {
              return param.status === EmailCampaignStatus.ARCHIVED;
            }
          },
          items: [
            {
              label: 'common_edit',
              onClick: emailCampaign => this.router.navigate(['/mail/campaign-setup', emailCampaign.idCampaign])
            },
            {
              label: 'campaign_list_arch',
              onClick: e => this.openModalArchive(e)
            }
          ]
        },
        cellClass: ['button-group', 'text-end'],
        maxWidth: 82
      }
    ];
  }

  private getHtmlRenderingTitleAndDescription(data) {
    let text = '';
    const emailCampagn: EmailCampaign = EmailCampaign.deserialize(data);
    if (emailCampagn) {
      text = text + this.getHtmlRenderingTitle(emailCampagn.title);
      text = text + this.getHtmlRenderingDescription(emailCampagn.description);
    }
    return '<div class ="rendering-title">' + text + '</div>';
  }

  private getHtmlRenderingTitle(valueTitle) {
    const title = valueTitle ? valueTitle : this.stringEmpty;
    return '<span><b>' + title + '</b></span><br>';
  }

  private getHtmlRenderingDescription(value) {
    const desc = value ? value : this.stringEmpty;
    return '<span>' + desc + '</span><br>';
  }

  private getHtmlRenderingStatus(data) {
    const emailCampagn: EmailCampaign = EmailCampaign.deserialize(data);
    const status = emailCampagn.status;
    switch (status) {
      case EmailCampaignStatus.ACTIVE:
        return this.getHtmlRenderStatus(EmailCampaignIdLabel.LABEL_ACTIVE, EmailStatusHTMLTagSpan.ACTIVE);
      case EmailCampaignStatus.ARCHIVED:
        return this.getHtmlRenderStatus(EmailCampaignIdLabel.LABEL_ARCHIVED, EmailStatusHTMLTagSpan.ARCHIVED);
      case EmailCampaignStatus.TO_ACTIVATE:
        return this.getHtmlRenderStatus(EmailCampaignIdLabel.LABEL_TO_ACTIVATE, EmailStatusHTMLTagSpan.TO_ACTIVATE);
    }
  }

  private getHtmlRenderStatus(idLabel: string, spanHTMLStatus) {
    const label = this.getTranslateLabels(idLabel);
    return spanHTMLStatus + label + '</span>';
  }

  private getTranslateLabels(value: string) {
    if (value) {
      let labels;
      this.translateService.get(value).subscribe(label => {
        labels = label;
      });
      return labels;
    }
  }

  openModalArchive(data: any): void {
    const emailCampagn: EmailCampaign = EmailCampaign.deserialize(data);
    this.idCampaignToArchive = emailCampagn.idCampaign;
    this.modalRefArchive = this.modalService.open(this.modalArchive);
    this.modalRefArchive.result.then(
      result => {},
      reason => {}
    );
  }

  modalArchivedCampaign() {
    this.callApiArchivedCampign(this.idCampaignToArchive);
    this.modalRefArchive.close();
  }

  modalArchivedDismiss() {
    this.modalRefArchive.close();
    this.reloadList();
  }

  private callApiArchivedCampign(idCampaign: number) {
    this.emailListService.archavieEmailCampaign(idCampaign).subscribe(
      success => {
        this.atlasToast.success('common_success', null, 6000, this.toastContainer);
        this.reloadList();
      },
      error => {
        this.atlasToast.error('common_error', null, 6000, this.toastContainer);
        this.reloadList();
      }
    );
  }

  handlerCreateListEmail() {}
}
